import { useEffect, useState } from "react";
import { Loader2, Plus, ShieldOff, X } from "lucide-react";
import { toast } from "sonner";
import bizApi, { apiError } from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { btnGhost, btnPrimary, btnSecondary, cardCls, inputCls, labelCls } from "./ui";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", language: "", company: "", title: "", notes: "", preferences: [] };

export default function Guests({ onBookFor }) {
  const { t } = useBiz();
  const [guests, setGuests] = useState(null);
  const [editing, setEditing] = useState(null); // null | EMPTY | guest
  const [prefText, setPrefText] = useState("");

  const load = () => bizApi.get("/guests").then((r) => setGuests(r.data)).catch(() => setGuests([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, preferences: prefText.split("·").map((p) => p.trim()).filter(Boolean) };
    try {
      if (editing.id) {
        await bizApi.patch(`/guests/${editing.id}`, payload);
      } else {
        await bizApi.post("/guests", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, t("common.error")));
    }
  };

  const anonymize = async (g) => {
    if (!window.confirm(t("guests.anonymize_confirm"))) return;
    await bizApi.post(`/guests/${g.id}/anonymize`).catch(() => {});
    load();
  };

  const remove = async (g) => {
    await bizApi.delete(`/guests/${g.id}`).catch(() => {});
    load();
  };

  return (
    <div data-testid="guests-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">{t("guests.title")}</h1>
          <p className="mt-0.5 text-sm text-slate">{t("guests.subtitle")}</p>
        </div>
        <button type="button" onClick={() => { setEditing({ ...EMPTY }); setPrefText(""); }}
          className={btnPrimary} data-testid="guest-new">
          <Plus className="h-4 w-4" /> {t("guests.new")}
        </button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {!guests && <div className="col-span-full flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div>}
        {guests && guests.length === 0 && (
          <div className={`${cardCls} col-span-full p-10 text-center`} data-testid="guests-empty">
            <p className="text-sm text-slate">{t("guests.empty")}</p>
          </div>
        )}
        {(guests || []).map((g) => (
          <div key={g.id} className={`${cardCls} p-5`} data-testid={`guest-card-${g.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-ink">{g.first_name} {g.last_name}</p>
                <p className="text-xs text-slate">{[g.company, g.title].filter(Boolean).join(" · ")}</p>
              </div>
              <span className="rounded-full bg-miles-ice px-2 py-0.5 text-[10px] font-semibold text-miles">
                {g.trips || 0} {t("guests.trips")}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate">{[g.phone, g.email].filter(Boolean).join(" · ")}</p>
            {(g.preferences || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {g.preferences.map((p, i) => (
                  <span key={i} className="rounded-full bg-cloud px-2 py-0.5 text-[10px] text-slate">{p}</span>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-ink/[0.06] pt-3">
              <button type="button" onClick={() => onBookFor(g)} className={btnGhost} data-testid={`guest-book-${g.id}`}>
                {t("guests.book")}
              </button>
              <button type="button" onClick={() => { setEditing(g); setPrefText((g.preferences || []).join(" · ")); }}
                className={btnGhost} data-testid={`guest-edit-${g.id}`}>{t("guests.edit")}</button>
              <button type="button" onClick={() => anonymize(g)} className={btnGhost} data-testid={`guest-anonymize-${g.id}`}>
                <ShieldOff className="h-3 w-3" /> {t("guests.anonymize")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-[2px]" data-testid="guest-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-ink">{editing.id ? t("guests.edit") : t("guests.new")}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="guest-modal-close"><X className="h-4 w-4 text-slate" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[["first_name", t("guests.first_name")], ["last_name", t("guests.last_name")],
                ["phone", t("guests.phone")], ["email", t("guests.email")],
                ["language", t("guests.language")], ["company", t("guests.company")],
                ["title", t("guests.title_field")]].map(([k, lab]) => (
                <div key={k}>
                  <label className={labelCls}>{lab}</label>
                  <input className={`${inputCls} h-10`} value={editing[k] || ""} data-testid={`guest-form-${k}`}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="mt-2">
              <label className={labelCls}>{t("guests.preferences")}</label>
              <input className={inputCls} placeholder={t("guests.pref_hint")} value={prefText}
                onChange={(e) => setPrefText(e.target.value)} data-testid="guest-form-preferences" />
            </div>
            <div className="mt-2">
              <label className={labelCls}>{t("guests.notes")}</label>
              <input className={inputCls} value={editing.notes || ""} data-testid="guest-form-notes"
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </div>
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={save} className={`${btnPrimary} flex-1`} data-testid="guest-form-save">{t("guests.save")}</button>
              {editing.id && (
                <button type="button" onClick={() => { remove(editing); setEditing(null); }}
                  className={btnSecondary} data-testid="guest-form-delete">{t("guests.delete")}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
